/**
 * v-noautofill — stop the browser (and password managers) from autofilling saved
 * credentials into administration forms.
 *
 * Why this exists: admin credential fields (IMAP/SMTP passwords, API keys) render EMPTY —
 * the backend never sends the stored secret back. Chrome happily autofills a saved
 * password for the domain into that empty, masked field, and on save that autofilled value
 * silently overwrites the real stored secret. That is exactly how a working mailbox
 * password got replaced by an unrelated 12-char saved credential in production.
 *
 * `autocomplete="off"` does NOT stop this — Chrome deliberately ignores it on password
 * fields when it has a saved login. The reliable signal is `autocomplete="new-password"`,
 * plus the well-known password-manager opt-out attributes.
 *
 * Applied ONCE to the authenticated admin shell (`.authenticated` in Auth.vue), so it
 * covers every admin form — including inputs added later in drawers, tabs and v-if blocks
 * (a MutationObserver catches those) — while the login and public flows, which live in
 * separate branches, keep their normal password-manager behaviour.
 */

const PROCESSED = '__noAutofillProcessed';

const harden = (input) => {
    if (!input || input[PROCESSED]) {
        return;
    }
    input[PROCESSED] = true;

    const isPassword = input.type === 'password';
    // new-password is the one value Chrome honours for "do not fill a saved password";
    // off is enough to discourage autofill on the remaining text fields.
    input.setAttribute('autocomplete', isPassword ? 'new-password' : 'off');

    // Opt out of the common password managers too (LastPass, 1Password, Dashlane).
    input.setAttribute('data-lpignore', 'true');
    input.setAttribute('data-1p-ignore', 'true');
    input.setAttribute('data-form-type', 'other');
};

const hardenTree = (root) => {
    if (!root) {
        return;
    }
    if (root.tagName === 'INPUT') {
        harden(root);
    }
    if (root.querySelectorAll) {
        root.querySelectorAll('input').forEach(harden);
    }
};

export default {
    inserted: (el) => {
        hardenTree(el);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        hardenTree(node);
                    }
                });
            });
        });
        observer.observe(el, { childList: true, subtree: true });
        el.__noAutofillObserver = observer;
    },
    unbind: (el) => {
        if (el.__noAutofillObserver) {
            el.__noAutofillObserver.disconnect();
            delete el.__noAutofillObserver;
        }
    },
};
